import { Properties } from '../Properties'
import { HeroImage } from '../HeroImage'
import { ContentBlocks } from '../ContentBlocks'
import { PromotionalCard } from '../PromotionalCard'
import { Goals } from '../../markets/Goals'
import { ImageList } from '../../markets/ImageList'
import { DuoImages } from '../DuoImages'
import { styles } from './FlexibleContent.css'
import clsx from 'clsx'
import { InformationTable } from '../InformationTable'
import { Circularity } from '@otomoro/components/technology/Circularity/Circularity'
import { ListBlock } from '@otomoro/components/technology/ListBlock/ListBlock'
import { SliderSection } from '@otomoro/components/technology/ImageSlider/SliderSection'
import { TextDuoImages } from '@otomoro/components/company/TextDuoImages'
import { Investors } from '@otomoro/components/company/Investors'
import { DateCards } from '@otomoro/components/company/DateCards'
import { Quote } from '../Quote'
import { Manifesto } from '@otomoro/components/company/Manifesto'
import { BannerCards } from '@otomoro/components/markets/BannerCards/BannerCards'
import { SingleImage } from '../SingleImage/SingleImage'
import { TextHighlight } from '../TextHighlight'
import { CircularSchemas } from '../CircularSchemas/CircularSchemas'
import { ImageHighlight } from '@otomoro/components/company/ImageHighlight/ImageHighlight'
import { SchemaSection } from '@otomoro/components/technology/SchemaSection/SchemaSection'
import { KeyFigures } from '@otomoro/components/home/keyFigures'
import { AlternateSection } from '@otomoro/components/home/AlternateSection'
import { HighlightedCards } from '@otomoro/components/home/HighlightedCards'
import { Gallery } from '../Gallery/Gallery'
import { HighlightedSection } from '@otomoro/components/home/HighlightedSection'
import { CardsSlider } from '@otomoro/components/home/CardsSlider'

export type FlexibleContentProps = {
	blocks: (
		| Sanity.Default.Schema.Mission
		| Sanity.Default.Schema.CardsSlider
		| Sanity.Default.Schema.HighlightedCards
		| Sanity.Default.Schema.HighlightedSection
		| Sanity.Default.Schema.Goals
		| Sanity.Default.Schema.ImageList
		| Sanity.Default.Schema.DuoImages
		| Sanity.Default.Schema.ImageSlider
		| Sanity.Default.Schema.ListBlock
		| Sanity.Default.Schema.Circularity
		| Sanity.Default.Schema.TextDuoImages
		| Sanity.Default.Schema.Investors
		| Sanity.Default.Schema.DateCards
		| Sanity.Default.Schema.Quote
		| Sanity.Default.Schema.Goals
		| Sanity.Default.Schema.ListCards
		| Sanity.Default.Schema.ContentBlocks
		| Sanity.Default.Schema.PromotionalCard
	)[]
}

export const FlexibleContent: React.FC<FlexibleContentProps> = ({ blocks }) => {
	if (!blocks) return null

	const { section } = styles()

	return (
		<div>
			{blocks.map((block: any, index: number) => {
				switch (block._type) {
					case 'highlightedSection':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'highlightedSection')}>
								<HighlightedSection
									tag={block.tag}
									headline={block.headline}
									description={block.description}
									button={block.button}
								/>
							</div>
						)
					case 'imageSlider':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'imageSlider')}>
								<SliderSection
									tag={block.tag}
									titleText={block.title}
									description={block.description}
									images={block.images}
								/>
							</div>
						)
					case 'cardsSlider':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'cardsSlider')}>
								<CardsSlider
									title={block.title}
									headline={block.headline}
									cardsList={block.products}
									theme={block.darkMode ? 'dark' : 'light'}
								/>
							</div>
						)
					case 'schemaSection':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'schemaSection')}>
								<SchemaSection
									tag={block.tag}
									headline={block?.headline as any}
									steps={block.steps as any}
								/>
							</div>
						)
					case 'listBlock':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'listBlock')}>
								<ListBlock
									tag={block.tag}
									titleText={block.title}
									description={block.description}
									list={block.list}
								/>
							</div>
						)
					case 'circularity':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'circularity')}>
								<Circularity
									title={block.title}
									illustration={block.illustration}
									characteristics={block.characteristics}
								/>
							</div>
						)
					case 'textHighlight':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'textHighlight')}>
								<TextHighlight tag={block.tag} introduction={block.introduction} />
							</div>
						)
					case 'heroImage':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'heroImage')}>
								<HeroImage
									key={index}
									tag={block.tag}
									hero={block.hero}
									picture={block.brandThumbnail}
								/>
							</div>
						)
					case 'contentBlocks':
						return (
							<div
								key={`fc-${index}`}
								className={clsx(section(), 'contentBlocks', {
									contentBlocksLowMt: !block?.introduction,
								})}
							>
								<ContentBlocks
									tag={block.tag}
									title={block.introduction}
									description={block.description}
									blocks={block.blocks}
									offset={block.push}
									fullWidth={block.fullWidth}
								/>
							</div>
						)
					case 'properties':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'properties')}>
								<Properties
								  _key={block._key}
									tag={block.tag}
									title={block.title}
									description={block.description}
									properties={block.properties}
									display3d={block.display3d}
									model3d={block.model3d}
									illustration={block.illustration}
									downloadInfo={block.downloadInfo}
								/>
							</div>
						)
					case 'promotionalCard':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'promotionalCard')}>
								<PromotionalCard
									tag={block.tag}
									title={block.title}
									description={block.description}
									button={block.button}
								/>
							</div>
						)
					case 'informationTable':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'informationTable')}>
								<InformationTable
									tag={block.tag}
									title={block.title}
									description={block.description}
									table={block.table}
								/>
							</div>
						)
					case 'goals':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'goals')}>
								<Goals
									tag={block.tag}
									title={block.title}
									description={block.description}
									goals={block.goals}
								/>
							</div>
						)
					case 'imageList':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'imageList')}>
								<ImageList tag={block.tag} title={block.title} imageList={block.imageList} />
							</div>
						)
					case 'duoImages':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'textDuoImages')}>
								<DuoImages largeImage={block.largeImage} smallImage={block.smallImage} />
							</div>
						)
					case 'textDuoImages':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'textDuoImages')}>
								<TextDuoImages
									tag={block.tag}
									titleText={block.title}
									description={block.description}
									orientation={block.orientation}
									largeImage={block.largeImage}
									smallImage={block.smallImage}
								/>
							</div>
						)
					case 'investors':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'investors')}>
								<Investors tag={block.tag} title={block.title} investors={block.investors} />
							</div>
						)
					case 'highlightedCards':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'highlightedCards')}>
								<HighlightedCards
									key={`fc-${index}`}
									_key={block._key}
									title={block.title}
									headline={block.headline}
									expandedMarkets={true}
									cards={block.cards}
									formOption={block.formOption}
									downloadInfo={block.downloadInfo}
								/>
							</div>
						)
					case 'dateCards':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'dateCards')}>
								<DateCards
									key={index}
									title={block.title}
									headline={block.headline}
									dateCards={block.cards}
									description={block.description}
								/>
							</div>
						)
					case 'keyFigures':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'relative keyFigures')}>
								<KeyFigures
									key={`fc-${index}`}
									tag={block.tag}
									headline={block.headline}
									description={block.description}
									cards={block.cards}
								/>
							</div>
						)
					case 'alternateSection':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'alternateSection')}>
								<AlternateSection
									_key={block._key}
									tag={block.tag}
									headline={block.headline}
									description={block.description}
									button={block.button}
									image={block.image}
									alternateSide={block.alternateSide}
									theme={block.darkMode ? 'dark' : 'light'}
									descriptionType={block.descriptionType}
									descriptionPairs={block.descriptionPairs}
									showDL={block.showDownload}
									downloadInfo={block.downloadInfo}
									showLogo={block.showLogo}
									cardLogo={block.cardLogo}
								/>
							</div>
						)
					case 'quote':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'quote')}>
								<Quote
									quote={block.primaryText}
									author={block.authorName}
									profession={block.authorProfession}
								/>
							</div>
						)
					case 'manifesto':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'manifesto')}>
								<Manifesto
									tag={block.tag}
									headline={block.headline}
									showLogo={block.showLogo}
									leftContent={block.leftContent}
									rightContent={block.rightContent}
								/>
							</div>
						)
					case 'circularSchemas':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'circularSchemas')}>
								<CircularSchemas
									_key={block._id}
									tag={block.tag}
									headline={block.headline}
									downloadInfo={block.downloadInfo}
									legendsTitle={block.legendsTitle}
									legends={block.legends}
									circularGraphs={block.circularGraphs}
								/>
							</div>
						)
					case 'bannerCards':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'bannerCards')}>
								<BannerCards
									key={index}
									tag={block.tag}
									headline={block.headline}
									productsList={block.products}
								/>
							</div>
						)
					case 'singleImage':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'singleImage')}>
								<SingleImage
									_key={block._key}
									image={block.image}
									displayText={block.displayText}
									tag={block.tag}
									headline={block.headline}
									content={block.content}
									displayForm={block.displayForm}
									labelBtn={block.labelBtn}
									titleForm={block.titleForm}
									checkboxText={block.checkboxText}
									downloadInfo={block.downloadInfo}
								/>
							</div>
						)
					case 'imageHighlight':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'imageHighlight')}>
								<ImageHighlight image={block.image} headline={block.headline} logo={block.logo} />
							</div>
						)
					case 'gallery':
						return (
							<div key={`fc-${index}`} className={clsx(section(), 'gallery overflow-hidden')}>
								<Gallery tag={block.tag} hasArrows={block.hasArrows} gallery={block.gallery} />
							</div>
						)
					default:
						return null
				}
			})}
		</div>
	)
}
